import { useEffect, useState } from "react";
import { t } from "i18next";
import GridDX from "../layout/griddx";
import AddEditModalDX from "../business/addeditmodaldx";
import SelectListDX from "../controls/selectlistdx";
import Addresspicker from "../business/addresspicker";
import { useNotificationContext } from "../../context/notificationcontext";
import useCustomerAddressService from "../../shared/services/customeraddressservice";
import useWorldService from "../../shared/services/worldservice";

interface Props {
  open: boolean;
  handleClose: (saved?: boolean) => void;
  customerId: string;
}

const AddCustomerAddressModal = ({ open, handleClose, customerId }: Props) => {
  const { addCustomerAddress } = useCustomerAddressService();
  const { getCitiesByCountryId } = useWorldService();

  const { setInfo, setError } = useNotificationContext();

  const [cities, setCities] = useState<{ text: string; value: string }[]>([]);
  const [cityId, setCityId] = useState("");
  const [location, setLocation] = useState({
    streetAddress: "",
    lat: 24.7136,
    lng: 46.6753,
  });
  const [errors, setErrors] = useState<{ city?: string; street?: string }>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isChanged, setIsChanged] = useState(false);

  useEffect(() => {
    if (open) {
      getCitiesByCountryId()
        .then((list) =>
          setCities(
            list.map((c: any) => ({ text: c.cityName, value: c.cityId })),
          ),
        )
        .catch(setError);
      // reset form whenever opened
      setCityId("");
      setLocation({ streetAddress: "", lat: 24.7136, lng: 46.6753 });
      setErrors({});
      setIsChanged(false);
    }
  }, [open]);

  const validate = () => {
    const errs: typeof errors = {};
    if (!cityId) errs.city = t("City is required");
    if (!location.streetAddress) errs.street = t("Street Address is required");
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const onSave = () => {
    if (!validate()) return;

    setIsSaving(true);
    const payload = {
      customerId,
      cityId,
      streetAddress: location.streetAddress,
      locationCoord: `${location.lat},${location.lng}`,
    };

    addCustomerAddress(payload)
      .then(() => {
        setInfo(t("Customer Address added successfully"));
        handleClose(true);
      })
      .catch(setError)
      .finally(() => setIsSaving(false));
  };

  return (
    <AddEditModalDX
      open={open}
      title={t("Add New Address")}
      handleClose={() => handleClose(false)}
      isSaving={isSaving}
      isChanged={isChanged}
      onSaveClick={onSave}
      onYesClick={() => handleClose(false)}
      maxWidth="sm"
    >
      <GridDX container spacing={2} sx={{ pt: 1 }}>
        <GridDX item xs={12} md={6}>
          <SelectListDX
            label={t("City")}
            items={cities}
            value={cityId}
            onChange={(e: any) => {
              setCityId(e.target.value);
              setIsChanged(true);
            }}
            error={errors.city}
          />
        </GridDX>
        <GridDX item xs={12}>
          <Addresspicker
            location={location}
            setLocation={(loc: any) => {
              setLocation(loc);
              setIsChanged(true);
            }}
            onAddressChange={() => setIsChanged(true)}
            emptyAddressError={errors.street}
          />
        </GridDX>
      </GridDX>
    </AddEditModalDX>
  );
};

export default AddCustomerAddressModal;
